/**
 * Pre-launch routing gate — Early Access / Waitlist v1.
 *
 * When ``window.__STOCKAILY_RUNTIME_CONFIG__.publicPrelaunchMode`` is
 * truthy, unauthenticated visitors hitting the pre-launch marketing
 * and product routes are redirected to ``/early-access``.
 *
 * Allowed through regardless of the flag (never gated):
 *   * ``/early-access`` and ``/waitlist``
 *   * Auth surface       — ``/login``, ``/forgot-password``,
 *                          ``/reset-password``, ``/auth/*``
 *                          (``/signup`` is explicitly GATED while
 *                          the pre-launch flag is on — new public
 *                          accounts are not accepted pre-launch).
 *   * DOI / email        — ``/api/email/consent/confirm`` is backend;
 *                          the UI preference / unsubscribe page
 *                          ``/email-preferences`` and legal pages must
 *                          be reachable.
 *   * Legal pages        — ``/de/*``, ``/en/*``, ``/legal/*``,
 *                          ``/research-transparency``
 *   * Owner console      — ``/owner-console``, ``/admin/*``, ``/settings/*``
 *
 * Gated routes (unauthenticated → ``/early-access``):
 *   * ``/``              (landing)
 *   * ``/pricing``
 *   * ``/signup``        (pre-launch — no new public accounts)
 *   * ``/billing``
 *   * ``/analyze``
 *   * ``/portfolio``
 *   * ``/watchlist``
 *
 * Production locks every gated route, including for authenticated
 * users. Staging / preview keep the authenticated-user bypass so
 * the team can continue testing the full application.
 */
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { resolveAppEnvironment } from "@/lib/appEnvironment";

// Explicitly gated prefixes.  Pathname === p OR pathname starts with p + "/".
const GATED_PREFIXES = [
  "/pricing",
  "/signup",
  "/billing",
  "/analyze",
  "/portfolio",
  "/watchlist",
];

// Prefixes that must ALWAYS resolve regardless of the flag.
// NOTE: ``/signup`` is NOT in this list — it is explicitly gated above.
const EXEMPT_PREFIXES = [
  "/early-access",
  "/waitlist",
  "/login",
  "/forgot-password",
  "/reset-password",
  "/auth",
  "/email-preferences",
  "/legal",
  "/de/",
  "/en/",
  "/research-transparency",
  "/owner-console",
  "/admin",
  "/settings",
];

export function isPrelaunchModeEnabled() {
  try {
    if (typeof window === "undefined") return false;
    const rc = window.__STOCKAILY_RUNTIME_CONFIG__;
    return !!(rc && rc.publicPrelaunchMode === true);
  } catch {
    return false;
  }
}

function isExempt(pathname) {
  if (!pathname) return false;
  if (pathname === "/early-access" || pathname === "/waitlist") return true;
  for (const p of EXEMPT_PREFIXES) {
    if (p === "/") continue;
    if (pathname === p) return true;
    if (pathname.startsWith(p === "/" ? "/" : p + (p.endsWith("/") ? "" : "/"))) return true;
  }
  return false;
}

function isGated(pathname) {
  if (!pathname) return false;
  // Root is a bounded exact match (not a prefix) so arbitrary paths
  // (e.g. ``/foo``) are not swallowed as "gated" — unknown routes
  // continue to render 404s normally.
  if (pathname === "/") return true;
  for (const p of GATED_PREFIXES) {
    if (pathname === p) return true;
    if (pathname.startsWith(p + "/")) return true;
  }
  return false;
}

/**
 * Wraps a route element. When the runtime flag is on:
 *   * production redirects every gated route, regardless of auth;
 *   * staging / preview retain the authenticated-user bypass;
 *   * exempt and unknown routes keep their normal behaviour.
 */
export default function PrelaunchGate({ children }) {
  const location = useLocation();
  const { user, loading } = useAuth();

  if (!isPrelaunchModeEnabled()) return children;
  if (isExempt(location.pathname)) return children;
  if (!isGated(location.pathname)) return children;

  // Production is a public pre-launch surface: even an existing
  // authenticated session must not expose the product routes. Do not
  // wait for auth loading here, which also prevents a brief app flash.
  if (resolveAppEnvironment() === "production") {
    return <Navigate to="/early-access" replace />;
  }

  // Staging / preview remain usable for the team and existing test
  // accounts while public pre-launch mode is enabled.
  if (loading) return children;
  if (user) return children;

  return <Navigate to="/early-access" replace />;
}
